/**
 * BriefsView — committed-run brief inspector.
 *
 * Lists the briefs a *real* (non-dry-run) strategic_rebuild produced for
 * this client and lets you drill into each one: objective, insight,
 * synthesized strategy, angles, and the source-creative lineage that fed
 * the synthesis. Read-only — tuning/preview lives on the Tuning tab.
 */
import { useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { ChevronRight, RefreshCw } from 'lucide-react';
import {
  generationApi,
  type GenerationBrief,
} from '@/services/api';
import { EmptyState } from '@/components/pdo/EmptyState';
import { ErrorBanner } from '@/components/pdo/ErrorBanner';
import { useFetch, timeAgo } from '@/components/pdo/utils';

const STATUSES = ['all', 'active', 'draft', 'archived', 'superseded'] as const;
type StatusFilter = (typeof STATUSES)[number];

function isStrategicRebuildBrief(b: GenerationBrief): boolean {
  return (
    !!b.strategic_cluster_id ||
    !!(b.metadata && b.metadata['strategic_rebuild_cluster_id'])
  );
}

function StatusPill({ status }: { status: string }) {
  const tone =
    status === 'active'
      ? 'bg-green-100 text-green-700'
      : status === 'draft'
        ? 'bg-amber-100 text-amber-700'
        : 'bg-slate-100 text-slate-600';
  return (
    <span
      className={clsx(
        'text-[11px] px-2 py-0.5 rounded-full font-medium',
        tone,
      )}
    >
      {status}
    </span>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </div>
      <div className="text-xs text-slate-700">{children}</div>
    </div>
  );
}

function asStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
}

function BriefDetail({ brief }: { brief: GenerationBrief }) {
  const b = brief as unknown as Record<string, unknown>;
  const objective = b.objective as Record<string, unknown> | undefined;
  const insight = b.insight as Record<string, unknown> | undefined;
  const strategy = b.synthesized_strategy as
    | Record<string, unknown>
    | undefined;
  const angles = (b.selected_angles as Array<Record<string, unknown>>) ?? [];
  const sourceIds = (b.source_creative_ids as string[]) ?? [];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {brief.description && (
        <Section label="Description">
          <p>{brief.description}</p>
        </Section>
      )}
      {objective && (
        <Section label="Objective">
          <p>
            {asStr(objective.statement) || asStr(objective.campaign_type)}
          </p>
        </Section>
      )}
      {insight && (
        <Section label="Insight">
          <p>{asStr(insight.statement) || asStr(insight)}</p>
        </Section>
      )}
      {strategy && (
        <Section label="Synthesized strategy">
          <p className="font-medium">{asStr(strategy.headline)}</p>
          <p className="mt-1 text-slate-600">
            {asStr(strategy.strategic_thesis)}
          </p>
        </Section>
      )}
      {angles.length > 0 && (
        <Section label={`Angles (${angles.length})`}>
          <ul className="space-y-1 list-disc pl-4">
            {angles.slice(0, 6).map((a, i) => (
              <li key={i}>
                <span className="font-medium">{asStr(a.hook)}</span>
                {a.rationale ? ` — ${asStr(a.rationale)}` : ''}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <Section label={`Source creatives (${sourceIds.length})`}>
        {sourceIds.length === 0 ? (
          <span className="text-slate-400">none recorded</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {sourceIds.slice(0, 30).map((id) => (
              <span
                key={id}
                className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded"
              >
                {id}
              </span>
            ))}
            {sourceIds.length > 30 && (
              <span className="text-slate-400 text-[10px]">
                +{sourceIds.length - 30} more
              </span>
            )}
          </div>
        )}
      </Section>
    </div>
  );
}

export function BriefsView({ clientUid }: { clientUid: string }) {
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [open, setOpen] = useState<string | null>(null);

  const query = useFetch(
    () => generationApi.listBriefs({ client_uid: clientUid, limit: 100 }),
    [clientUid],
  );

  const briefs = useMemo(() => {
    const all = (query.data?.briefs ?? []).filter(
      (b) => isStrategicRebuildBrief(b) && b.client_uid === clientUid,
    );
    const f =
      filter === 'all' ? all : all.filter((b) => b.status === filter);
    return [...f].sort((a, z) =>
      (z.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
  }, [query.data, filter, clientUid]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={clsx(
                'text-xs px-3 py-1.5 rounded-lg border',
                filter === s
                  ? 'bg-brand-teal text-white border-brand-teal'
                  : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50',
              )}
            >
              {s}
            </button>
          ))}
        </div>
        <button
          onClick={() => query.refetch()}
          className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800"
        >
          <RefreshCw
            className={clsx('w-3.5 h-3.5', query.loading && 'animate-spin')}
          />
          Refresh
        </button>
      </div>

      {query.error && (
        <ErrorBanner error={query.error} onRetry={query.refetch} />
      )}

      {!query.loading && briefs.length === 0 && !query.error && (
        <EmptyState
          title="No committed strategic-rebuild briefs"
          hint="Briefs appear here after a real (non-dry-run) strategic rebuild persists them. Use the Tuning tab to simulate without committing."
        />
      )}

      <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
        {briefs.map((b) => {
          const isOpen = open === b.brief_id;
          return (
            <div key={b.brief_id}>
              <button
                onClick={() => setOpen(isOpen ? null : b.brief_id)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50"
              >
                <ChevronRight
                  className={clsx(
                    'w-4 h-4 text-slate-400 transition-transform shrink-0',
                    isOpen && 'rotate-90',
                  )}
                />
                <span className="text-sm font-medium text-slate-800 truncate">
                  {b.name || b.brief_id}
                </span>
                <StatusPill status={b.status} />
                <span className="ml-auto text-xs text-slate-400 shrink-0">
                  {b.created_at ? timeAgo(b.created_at) : '—'}
                </span>
              </button>
              {isOpen && (
                <div className="px-6 pb-5 pt-1 bg-slate-50/60">
                  <BriefDetail brief={b} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
